import { useCallback } from "react";
import { useUserByGuid } from "@/hooks/useUserByGuid";
import { studyGroupsApi } from "@/lib/humhub-api/studyGroups";
import type { User } from "@/lib/humhub-api/types";

interface EntityInfo {
  type: "user" | "studyGroup";
  data: User | { id: string; name: string };
}

// エンティティ情報を取得する関数
const fetchEntityByGuid = async (
  guid: string,
  fetchUserByGuid: (guid: string) => Promise<User>,
): Promise<EntityInfo> => {
  try {
    // まずユーザーで試行
    const userInfo = await fetchUserByGuid(guid);
    return { type: "user", data: userInfo };
  } catch (_userError) {
    // ユーザーが見つからない場合、スタディグループで試行
    try {
      const studyGroupInfo = await studyGroupsApi.getStudyGroupInfoByGuid(guid);
      return {
        type: "studyGroup",
        data: { id: String(studyGroupInfo.id), name: studyGroupInfo.name },
      };
    } catch (_studyGroupError) {
      throw new Error(`Neither user nor study group found for guid ${guid}`);
    }
  }
};

export const useMentionLinkProcessor = () => {
  const { fetchUserByGuid } = useUserByGuid();

  const processMentionLinks = useCallback(
    async (content: string): Promise<string> => {
      const mentionLinkRegex =
        /\[([^\]]*)\]\(mention:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\s+[^)]+)?\)/g;

      const matches = Array.from(content.matchAll(mentionLinkRegex));

      if (matches.length === 0) {
        return content;
      }

      // マッチした項目のGUIDを重複排除
      const uniqueGuids = [...new Set(matches.map((match) => match[2]))];

      // 各GUIDの情報を並列取得（重複排除により効率化）
      const guidToEntityMap = new Map<string, EntityInfo>();

      await Promise.allSettled(
        uniqueGuids.map(async (guid) => {
          try {
            const entityInfo = await fetchEntityByGuid(guid, fetchUserByGuid);
            guidToEntityMap.set(guid, entityInfo);
          } catch (error) {
            console.warn(`Failed to fetch entity for guid ${guid}:`, error);
            // エラーの場合はマップに追加しない（後でfallback処理）
          }
        }),
      );

      // 一度の文字列操作で全てのマッチを置換
      let processedContent = content;

      for (const [fullMatch, linkText, guid] of matches) {
        const entityInfo = guidToEntityMap.get(guid);

        if (entityInfo) {
          let replacement: string;

          if (entityInfo.type === "user") {
            const userInfo = entityInfo.data as User;
            replacement = `[@${linkText}](/user-profile/${userInfo.id} "mention-user-${userInfo.id}")`;
          } else {
            const studyGroupInfo = entityInfo.data as {
              id: string;
              name: string;
            };
            replacement = `[@${linkText}](/all-study-groups/${studyGroupInfo.id} "mention-studygroup-${studyGroupInfo.id}")`;
          }

          processedContent = processedContent.replace(fullMatch, replacement);
        } else {
          // フォールバック：@付きの元のテキストを表示
          processedContent = processedContent.replace(
            fullMatch,
            `@${linkText}`,
          );
        }
      }

      return processedContent;
    },
    [fetchUserByGuid],
  );

  return { processMentionLinks };
};
